import React from 'react';
import { Wind, Home, Building, Briefcase } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';

const Products = () => {
  const products = [
    {
      icon: Home,
      title: 'Split AC',
      description:
        'Energy-efficient split air conditioners perfect for homes and small offices.',
      features: [
        'Energy Star Rated',
        'Silent Operation',
        '1-5 Ton Capacity',
        'Smart Controls',
      ],
      image: 'https://images.unsplash.com/photo-1762341123870-d706f257a12e',
    },
    {
      icon: Wind,
      title: 'Window AC',
      description:
        'Compact and affordable window units ideal for single rooms.',
      features: [
        'Easy Installation',
        'Cost Effective',
        '0.75-2 Ton',
        'Low Maintenance',
      ],
      image: 'https://images.unsplash.com/photo-1758798157512-f0a864c696c9',
    },
    {
      icon: Building,
      title: 'Commercial AC',
      description:
        'Heavy-duty systems designed for offices, shops, and commercial spaces.',
      features: [
        'High Capacity',
        'Durable Build',
        'Multi-zone Control',
        'Energy Efficient',
      ],
      image:
        'https://images.pexels.com/photos/16848596/pexels-photo-16848596.jpeg',
    },
    {
      icon: Briefcase,
      title: 'VRF / HVAC Systems',
      description:
        'Advanced variable refrigerant flow systems for large buildings.',
      features: [
        'Centralized Control',
        'Scalable Solution',
        'Optimal Performance',
        'Smart Management',
      ],
      image: 'https://images.unsplash.com/photo-1709432767122-d3cb5326911a',
    },
  ];

  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="products" className="py-20 bg-gray-50">
      <div className="container mx-auto px-4">
        {/* Section header */}
        <div className="text-center mb-16 space-y-4">
          <span className="text-primary font-semibold text-sm uppercase tracking-wider">
            Our Products
          </span>
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900">
            Premium AC Solutions
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Choose from our wide range of air conditioning products from leading
            brands.
          </p>
        </div>

        {/* Products grid */}
        <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {products.map((product, index) => (
            <Card
              key={index}
              className="group hover:shadow-2xl transition-all duration-300 border-0 overflow-hidden bg-white"
            >
              <div className="relative h-64 overflow-hidden">
                <img
                  src={product.image}
                  alt={product.title}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
                <div className="absolute top-4 left-4 bg-white/95 backdrop-blur-sm p-3 rounded-xl shadow-lg">
                  <product.icon className="text-primary" size={28} />
                </div>
              </div>
              <CardContent className="p-6 space-y-4">
                <h3 className="text-2xl font-bold text-gray-900 group-hover:text-primary transition-colors">
                  {product.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {product.description}
                </p>
                <div className="grid grid-cols-2 gap-3 pt-2">
                  {product.features.map((feature, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <div className="w-1.5 h-1.5 bg-primary rounded-full"></div>
                      <span className="text-sm text-gray-700 font-medium">
                        {feature}
                      </span>
                    </div>
                  ))}
                </div>
                <Button
                  onClick={scrollToContact}
                  variant="outline"
                  className="w-full mt-4 border-primary text-primary hover:bg-primary hover:text-white transition-all"
                >
                  Get Quote
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Brand logos section */}
        <div className="mt-16 text-center">
          <p className="text-gray-600 font-semibold mb-8">
            We Deal With Leading Brands
          </p>
          <div className="flex flex-wrap justify-center items-center gap-12 opacity-60">
            <div className="text-2xl font-bold text-gray-700">VOLTAS</div>
            <div className="text-2xl font-bold text-gray-700">DAIKIN</div>
            <div className="text-2xl font-bold text-gray-700">CARRIER</div>
            <div className="text-2xl font-bold text-gray-700">LG</div>
            <div className="text-2xl font-bold text-gray-700">SAMSUNG</div>
            <div className="text-2xl font-bold text-gray-700">O'GENERAL</div>

            <div className="text-2xl font-bold text-gray-700">BLUE STAR</div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Products;
